import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock } from 'lucide-react';
import apiService from '../../services/api';
import SiteFooter from "../SiteFooter";

const Qimen = () => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});

  // State for form fields
  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    birthYear: '',
    birthMonth: '9',
    birthDay: '10',
    birthHour: '20',
    type: '',
    content: '',
    predictYear: new Date().getFullYear(),
    predictMonth: new Date().getMonth() + 1,
    predictDay: new Date().getDate(),
    predictHour: new Date().getHours(),
    qimenType: '',
    qimenMethod: ''
  });

  // Dynamic Options
  const [typeOptions, setTypeOptions] = useState([]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const types = await apiService.getZhantiTypes();
        if (types && types.length > 0) {
          setTypeOptions(types);
        }
      } catch (error) {
        console.error("Failed to load zhanti types", error);
      }
    };
    fetchTypes();
  }, []);

  const qimenTypeOptions = [
    { value: "转盘时家奇门", label: "转盘时家奇门" },
    { value: "飞盘奇门", label: "飞盘奇门" }
  ];

  const qimenMethodOptions = [
    { value: "拆补法", label: "拆补法" },
    { value: "置闰法", label: "置闰法" },
    { value: "茅山法", label: "茅山法" }
  ];

  // Handlers
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'birthYear') {
        const digitsOnly = value.replace(/\D/g, '').slice(0, 4);
        setFormData(prev => ({ ...prev, birthYear: digitsOnly }));
        setErrors(prev => ({ ...prev, birthYear: "", submit: "" }));
        return;
    }

    // 针对时间输入的校验
    if (['birthYear', 'birthMonth', 'birthDay', 'birthHour', 'predictYear', 'predictMonth', 'predictDay', 'predictHour'].includes(name)) {
        // 允许空值以便用户修改
        if (value === '') {
            setFormData(prev => ({ ...prev, [name]: value }));
            setErrors(prev => ({ ...prev, [name]: "", submit: "" }));
            return;
        }
        
        const num = parseInt(value);
        
        // 禁止非数字或负数
        if (isNaN(num) || num < 0) return;

        // 年份校验 > 2100
        if (name.includes('Year') && num > 2100) return;
        
        // 月份校验 1-12
        if (name.includes('Month') && (num > 12 || num === 0)) return;
        
        // 日期校验 1-31 (简单校验)
        if (name.includes('Day') && (num > 31 || num === 0)) return;
        
        // 小时校验 0-23
        if (name.includes('Hour') && num > 23) return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: "", submit: "" }));
  };

  const resetToCurrentTime = () => {
    const now = new Date();
    setFormData(prev => ({
      ...prev,
      predictYear: now.getFullYear(),
      predictMonth: now.getMonth() + 1,
      predictDay: now.getDate(),
      predictHour: now.getHours()
    }));
    setErrors(prev => ({ ...prev, predictYear: "", submit: "" }));
  };

  const handleSubmit = async () => {
    const err = {};

    if (!formData.type || !formData.content) {
      if (!formData.type) err.type = "请选择求占类型";
      if (!formData.content) err.content = "请输入求占内容";
    }

    const pYear = parseInt(formData.predictYear);
    if (pYear < 1900 || pYear > 2100) {
      err.predictYear = "起卦年份应在1900-2100之间";
    }

    if (formData.birthYear) {
      const bYear = parseInt(formData.birthYear);
      if (bYear < 1900 || bYear > 2100) {
        err.birthYear = "出生年份应在1900-2100之间";
      }
    }

    if (Object.keys(err).length > 0) {
      setErrors(err);
      return;
    }

    setErrors({});
    
    try {
        // 构造请求数据
        const requestData = {
            year: parseInt(formData.predictYear),
            month: parseInt(formData.predictMonth),
            day: parseInt(formData.predictDay),
            hour: parseInt(formData.predictHour),
            // 其他参数根据后端需求传递
        };

        const response = await axios.post('/api/qimen/calculate', requestData);
        
        if (response.data.success) {
            console.log("排盘成功:", response.data.data);
            // 导航到结果页，将数据通过 state 传递
            // 假设结果页路由为 /qimen/result
            navigate('/qimen/qimenDetail', { state: { result: response.data.data, form: formData } });
        } else {
          setErrors({ submit: "排盘失败: " + response.data.error });
        }
    } catch (error) {
        console.error("Error calculating qimen:", error);
        setErrors({ submit: "排盘请求失败，请检查网络或服务器状态" });
    }
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden md:max-w-2xl p-6 space-y-6">
        
        {/* Header */}
        <div className="text-center border-b pb-4">
          <h2 className="text-2xl font-bold text-gray-900">奇门遁甲排盘</h2>
        </div>

        {/* P1 Panel */}
        <div className="space-y-4">
          
          {/* Row 1: Prediction Time */}
          <div className="bg-indigo-50/50 p-3 rounded-xl border border-indigo-100">
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">预测时间</label>
              <button
                type="button"
                onClick={resetToCurrentTime}
                className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
              >
                <Clock className="h-4 w-4" />
                回到当前时间
              </button>
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div className="relative">
                <input type="number" name="predictYear" value={formData.predictYear} onChange={handleChange} className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 text-center" />
                <span className="absolute right-1 top-2.5 text-gray-400 text-xs pointer-events-none">年</span>
                {errors.predictYear && <div className="text-red-600 text-xs mt-1">{errors.predictYear}</div>}
              </div>
              <div className="relative">
                <input type="number" name="predictMonth" value={formData.predictMonth} onChange={handleChange} className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 text-center" />
                <span className="absolute right-1 top-2.5 text-gray-400 text-xs pointer-events-none">月</span>
              </div>
              <div className="relative">
                <input type="number" name="predictDay" value={formData.predictDay} onChange={handleChange} className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 text-center" />
                <span className="absolute right-1 top-2.5 text-gray-400 text-xs pointer-events-none">日</span>
              </div>
              <div className="relative">
                <input type="number" name="predictHour" value={formData.predictHour} onChange={handleChange} className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 text-center" />
                <span className="absolute right-1 top-2.5 text-gray-400 text-xs pointer-events-none">时</span>
              </div>
            </div>
          </div>

          {/* Row 2: Divination Info (Type & Content) */}
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className={`block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 bg-white ${formData.type ? "text-gray-700" : "text-gray-400"}`}
              >
                <option value="">占类</option>
                {typeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {errors.type && <div className="text-red-600 text-xs mt-1">{errors.type}</div>}
            </div>

            <div className="col-span-2">
              <input
                type="text"
                name="content"
                value={formData.content}
                onChange={handleChange}
                maxLength={50}
                className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5"
                placeholder="求占内容（限50 字）"
              />
              {errors.content && <div className="text-red-600 text-xs mt-1">{errors.content}</div>}
            </div>
          </div>

          {/* Row 3: Name, Gender, Birth Year */}
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-1">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5"
                placeholder="姓名"
              />
            </div>
            <div className="col-span-1">
              <select
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className={`block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-2.5 bg-white ${formData.gender ? "text-gray-700" : "text-gray-400"}`}
              >
                <option value="">性别</option>
                <option value="男">男</option>
                <option value="女">女</option>
              </select>
            </div>
            <div className="col-span-1 relative">
              <input type="text" name="birthYear" value={formData.birthYear} onChange={handleChange} inputMode="numeric" pattern="\d{4}" maxLength={4} className="block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 text-center placeholder-gray-400" placeholder="生年 YYYY" />
              <span className="absolute right-1 top-3 text-gray-400 text-xs pointer-events-none">年</span>
              {errors.birthYear && <div className="text-red-600 text-xs mt-1">{errors.birthYear}</div>}
            </div>
          </div>

          {/* Qimen Settings */}
          <div className="grid grid-cols-4 gap-2">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">奇门类型</label>
              <select
                name="qimenType"
                value={formData.qimenType}
                onChange={handleChange}
                className={`block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 bg-white ${formData.qimenType ? "text-gray-700" : "text-gray-400"}`}
              >
                <option value="">奇门类型</option>
                {qimenTypeOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">奇门方法</label>
              <select
                name="qimenMethod"
                value={formData.qimenMethod}
                onChange={handleChange}
                className={`block w-full h-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2.5 bg-white ${formData.qimenMethod ? "text-gray-700" : "text-gray-400"}`}
              >
                <option value="">奇门方法</option>
                {qimenMethodOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Buttons */}
          <div className="pt-4 flex justify-center">
            <button
              type="button"
              onClick={handleSubmit}
              className="px-8 py-2.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200 min-w-[120px]"
            >
              开始排盘
            </button>
          </div>

          {errors.submit && (
            <div className="text-red-600 text-xs text-center bg-red-50 p-1.5 rounded border border-red-100">
              {errors.submit}
            </div>
          )}
        </div>
      </div>
         <SiteFooter />
    </div>
  );
};

export default Qimen;